import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { RefreshCw, Download } from "lucide-react"
import { DatasourceBillsTable } from '@/components/DatasourceBillsTable'
import { SessionDisplay } from '@/components/SessionDisplay'
import type { APISession } from '@/types/session'
import type { DataSource } from '@/hooks/admin/useDatasourceState'

interface BillsTableCardProps {
  selectedDataSource: DataSource
  apiSessions: APISession[]
  billsSelectedSession: APISession | null
  onSessionChange: (value: string) => void
  onRefresh: () => void
  onLoadAll: () => void
  bills: any[]
  isLoading: boolean
  isLoadingAll: boolean
  allBillsLoaded: boolean
  allBillsCount: number
  currentPage: number
  pageSize: number
  totalPages: number
  onPageChange: (page: number) => void
  searchQuery: string
  onSearchChange: (query: string) => void
  totalFilteredItems: number
}

export function BillsTableCard({
  selectedDataSource,
  apiSessions,
  billsSelectedSession,
  onSessionChange,
  onRefresh,
  onLoadAll,
  bills,
  isLoading,
  isLoadingAll,
  allBillsLoaded,
  allBillsCount,
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
  searchQuery,
  onSearchChange,
  totalFilteredItems
}: BillsTableCardProps) {
  const getDataSourceIcon = (dataSource: DataSource) => {
    return dataSource === 'Maryland' ? '🏛️' : '🇺🇸'
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
            <div>
              <CardTitle className="text-xl flex items-center gap-3 text-slate-800 dark:text-slate-200">
                <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                  <span className="text-lg">{getDataSourceIcon(selectedDataSource)}</span>
                </div>
                {selectedDataSource} Bills
              </CardTitle>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                View raw bill data from {selectedDataSource} legislative data source
                {billsSelectedSession && (
                  <span className="ml-2 px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded-md text-xs font-medium">
                    {billsSelectedSession}
                  </span>
                )}
                {allBillsLoaded && (
                  <span className="ml-2 px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-md text-xs font-medium">
                    All {allBillsCount} bills loaded
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-3 items-center w-full sm:w-auto">
              <Select
                value={billsSelectedSession || undefined}
                onValueChange={onSessionChange}
              >
                <SelectTrigger className="w-[200px] h-9 border-slate-300 dark:border-slate-600">
                  <SelectValue placeholder="Session">
                    {billsSelectedSession && <SessionDisplay session={billsSelectedSession} />}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {apiSessions.map((session) => (
                    <SelectItem key={session} value={session}>
                      <SessionDisplay session={session} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                disabled={isLoading || isLoadingAll}
                className="flex items-center gap-2 h-9 px-3 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={onLoadAll}
                disabled={isLoading || isLoadingAll}
                className="flex items-center gap-2 h-9 px-3 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <Download className={`h-4 w-4 ${isLoadingAll ? 'animate-pulse' : ''}`} />
                {isLoadingAll ? (
                  <>
                    Loading... {allBillsCount > 0 && (
                      <span className="text-xs">({allBillsCount})</span>
                    )}
                  </>
                ) : allBillsLoaded ? (
                  <>Loaded All ({allBillsCount})</>
                ) : (
                  <>Load All</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {billsSelectedSession ? (
          <DatasourceBillsTable
            bills={bills}
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={onPageChange}
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            isLoading={isLoading}
            totalFilteredItems={totalFilteredItems}
            allBillsLoaded={allBillsLoaded}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="p-4 rounded-full bg-slate-100 dark:bg-slate-800 mb-4">
              <span className="text-3xl">{getDataSourceIcon(selectedDataSource)}</span>
            </div>
            <p className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
              Select Legislative Session
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md">
              Choose a legislative session to view raw bill data from the {selectedDataSource} data source.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
